import { ThemePalette } from 'shared-components/core/mixins';

import { SnackbarConfiguration, SnackbarConfigurationParameter, SnackbarIconConfiguration } from '../../models';
import { isPrimary } from '../color/snackbar-color.helper';

/**
 * Mapping of predefined icons to display, by color of Theme palette
 */
export const snackbarIconsColorMapping: { [type in ThemePalette]: string } = {
  primary: '',
  secondary: '',
  disabled: '',
  accent: '',
  info: 'circle-info',
  warning: 'triangle-exclamation',
  error: 'circle-xmark',
  success: 'circle-check',
};

/**
 * Transform an icon name or icon configuration in a proper icon configuration
 * @param {SnackbarConfigurationParameter} icon - configuration or icon name
 * @returns {SnackbarIconConfiguration} an icon configuration
 *  - an object with undefined name if no icon set: will be handled by the default config
 *  - an object with name if icon is only a string type: other fields will be filled by default configuration
 *  - an object with full configuration if param is a full configuration
 */
export const transformIconToConfiguration = (icon: SnackbarConfigurationParameter): SnackbarIconConfiguration => {
  if (!icon) {
    // NOTE: set to undefined because handled by default config later
    return { name: undefined };
  }

  if (typeof icon === 'string') {
    return { name: icon };
  }

  return icon;
};

/**
 * Extract and parse any icon configuration to a proper icon configuration with default values
 * @param {SnackbarConfigurationParameter} iconConfiguration - configuration or icon name
 * @returns {SnackbarIconConfiguration} full icon configuration with set or default values
 */
export const parseIconConfiguration = (iconConfiguration: SnackbarConfigurationParameter): SnackbarIconConfiguration => {
  const defaultConfig: Partial<SnackbarIconConfiguration> = {
    font: 'solid',
    size: 'xl',
    isSvg: false,
    sharp: false,
  };

  const { name, font, isSvg, sharp, size }: SnackbarIconConfiguration = transformIconToConfiguration(iconConfiguration);

  return {
    font: font || defaultConfig.font,
    isSvg: isSvg ?? defaultConfig.isSvg,
    sharp: sharp ?? defaultConfig.sharp,
    name: name ?? defaultConfig.name,
    size: size ?? defaultConfig.size,
  };
};

/**
 * Extract and parse full icon configuration for snackbar icon, from a snackbar configuration
 * @param {SnackbarConfiguration} configuration - configuration to parse
 * @returns {SnackbarIconConfiguration} parsed configuration for snackbar icon
 */
export const getIconConfiguration = (configuration: SnackbarConfiguration): SnackbarIconConfiguration => {
  const iconConfiguration: SnackbarConfigurationParameter = isPrimary(configuration)
    ? configuration.primaryConfiguration.icon
    : { name: snackbarIconsColorMapping[configuration.color] };

  return parseIconConfiguration(iconConfiguration);
};
